/* eslint-disable react-hooks/exhaustive-deps */
import { useMutation } from "@apollo/client";
import {
  Box,
  CircularProgress,
  Card as MuiCard,
  Typography,
  CardActionArea,
} from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import RoomServiceRoundedIcon from "@mui/icons-material/RoomServiceRounded";
import gql from "graphql-tag";
import React, { useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { addFavouriteRestaurant, profile } from "../../../apollo/server";
import UserContext from "../../../context/User";
import useStyles from "./styles";
import { useTranslation } from 'react-i18next';
import { restaurantOpenStatus } from "../../../utils/helper";
import { resizedImage } from "../../../utils/resizedImage";
import { CATERING_COLOR, cateringNotice } from "../../../utils/catering";

// Badge colors per open-status (DoorDash-style).
const STATUS_STYLE = {
  'open':         { bg: '#E7F7EC', color: '#1B873F', dot: '#1B873F' },
  'closing-soon': { bg: '#FFF4E5', color: '#B25E02', dot: '#F59E0B' },
  'opening-soon': { bg: '#FFF4E5', color: '#B25E02', dot: '#F59E0B' },
  'closed':       { bg: '#F3F4F6', color: '#6B7280', dot: '#9CA3AF' },
};

const ADD_FAVOURITE = gql`
  ${addFavouriteRestaurant}
`;
const PROFILE = gql`
  ${profile}
`;

function Card(props) {
  const { t } = useTranslation();
  const item = props.data ?? null;
  const navigateTo = useNavigate();
  const { profile } = useContext(UserContext);
  const heart = profile ? profile.favourite.includes(item?._id) : false;
  const cardImageHeight = props.cardImageHeight || "200px";
  const categories = item.categories?.map((category) => category.title) || [];
  const classes = useStyles(props);

  const [mutate, { loading }] = useMutation(ADD_FAVOURITE, {
    onCompleted,
    refetchQueries: [{ query: PROFILE }],
  });

  function onCompleted() {
    props.showMessage({
      type: "success",
      message: t('favouriteListUpdated'),
    });
  }

  // On the Catering page (props.catering) the card opens the restaurant's
  // Catering view.
  const navigate = useCallback(() => {
    if (
      props.checkCart(item._id, item.name, item.image, item.slug) &&
      !loading
    ) {
      navigateTo(`/restaurant/${item.slug}`, {
        state: {
          id: item._id,
          name: item.name,
          image: item.image,
          slug: item.slug,
          ...(props.catering ? { catering: true } : {}),
        },
      });
    }
  }, [navigateTo, item._id, props.catering]);

  const handleFavoriteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!profile) {
      props.showMessage({
        type: "error",
        message: "Please log in to add favorites",
      });
      return;
    }
    if (!loading) {
      mutate({ variables: { id: item._id } });
    }
  };

  const rating = item.reviewData?.ratings || 0;
  const reviewCount = item.reviewData?.reviews?.length || 0;
  const deliveryTime = item.deliveryTime || 25;
  const prepTime = item.defaultPrepTime || 15;
  const hasOffer = item.offers && item.offers.length > 0;
  const offerText = hasOffer ? item.offers[0]?.name || item.offers[0]?.tag : null;
  const activePromotionCount = item.activePromotionCount || 0;
  const hasPromotion = activePromotionCount > 0;

  // Convert km to miles (distance from backend is in km)
  const distanceMiles = item.distance != null
    ? (item.distance * 0.621371).toFixed(1)
    : null;

  // Live open/closed status (utils/helper.js restaurantOpenStatus).
  const isTruck = item.isMobile === true;
  const openStatus = restaurantOpenStatus(item);
  // "Today at Riverside Brewery" when parked, "Back Thursday at 11:00 AM" when not.
  const truckLine = isTruck
    ? (item.openStatusLabel || item.serviceLocation?.nextStopLabel || null)
    : null;
  const statusStyle = STATUS_STYLE[openStatus.status] || STATUS_STYLE.closed;
  const isClosed = openStatus.status === 'closed';

  // Catering page only (backend migration 131).
  const cateringDeal = props.catering && (item.cateringDealCount || 0) > 0;
  const cateringPickupOnly = props.catering && item.cateringDeliveryAvailable === false;
  const cateringLine = props.catering ? cateringNotice(item.catering?.leadTimeMinutes) : null;

  return (
    <MuiCard className={classes.card}>
      <CardActionArea
        onClick={(e) => {
          e.preventDefault();
          navigate();
        }}
      >
        <Box className={props.isSmall ? classes.smallWidth : classes.largeWidth}>
          {/* Image Container */}
          <Box
            className={classes.imageContainer}
            sx={{ height: cardImageHeight }}
          >
            <Box
              component="img"
              src={resizedImage(item.logo || item.image, props.isSmall ? 480 : 800)}
              alt={item.name}
              loading="lazy"
              decoding="async"
              className={classes.imgContainer}
              sx={{ height: cardImageHeight, filter: isClosed ? 'grayscale(60%) brightness(0.85)' : 'none' }}
            />

            {/* Status and, for a truck, what it is — sharing one row so the two
                badges can never overlap however long the status label gets. */}
            <Box
              sx={{
                position: 'absolute',
                top: 10,
                left: 10,
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
              }}
            >
              {/* Open / Closes soon / Opens soon / Closed status badge */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  px: '8px',
                  py: '3px',
                  borderRadius: '999px',
                  bgcolor: statusStyle.bg,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                }}
              >
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: statusStyle.dot }} />
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: statusStyle.color, lineHeight: 1 }}>
                  {openStatus.label}
                </Typography>
              </Box>

              {/* Food truck marker. Stays full colour even when the card is
                  greyed out for being closed — what it IS doesn't change with
                  whether it happens to be serving right now. */}
              {isTruck && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px',
                    px: '8px',
                    py: '3px',
                    borderRadius: '999px',
                    bgcolor: 'rgba(22,101,52,0.95)',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                  }}
                >
                  <Typography sx={{ fontSize: '0.7rem', lineHeight: 1 }}>🚚</Typography>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                    Food truck
                  </Typography>
                </Box>
              )}

              {/* Catering page: catering can't be delivered to this address,
                  only collected. */}
              {cateringPickupOnly && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: '8px',
                    py: '3px',
                    borderRadius: '999px',
                    bgcolor: '#fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                  }}
                >
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#334155', lineHeight: 1 }}>
                    Pickup only
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Offer / Promo Badge. On the Catering page a catering deal
                takes this spot. */}
            {cateringDeal ? (
              <Box
                className={classes.offerBadge}
                sx={{ display: 'flex', alignItems: 'center', gap: '4px', bgcolor: `${CATERING_COLOR} !important` }}
              >
                <RoomServiceRoundedIcon sx={{ color: '#fff', fontSize: 13 }} />
                <Typography className={classes.offerText}>Catering deal</Typography>
              </Box>
            ) : (hasOffer && offerText) || hasPromotion ? (
              <Box className={classes.offerBadge}>
                <Typography className={classes.offerText}>
                  {hasOffer && offerText ? offerText : `🏷 ${activePromotionCount} Deal${activePromotionCount !== 1 ? 's' : ''}`}
                </Typography>
              </Box>
            ) : null}

            {/* Heart button - top right on image */}
            <Box className={classes.heartBtn} onClick={handleFavoriteClick}>
              {loading ? (
                <CircularProgress size={14} sx={{ color: "#fff" }} />
              ) : heart ? (
                <FavoriteIcon className={classes.heartIcon} />
              ) : (
                <FavoriteBorderIcon className={classes.heartIconOutline} />
              )}
            </Box>

            {/* Closed state is conveyed by the status badge + grayscale image
                above, so no separate full-image "Currently Closed" overlay. */}
          </Box>

          {/* Content Section */}
          <Box className={classes.contentContainer}>
            {/* Restaurant Name */}
            <Typography className={classes.restaurantName} noWrap>
              {item.name}
            </Typography>

            {/* Food truck: where it is today, or when it is back. A truck's
                address changes daily, so this is the single most useful thing
                on the card. */}
            {truckLine && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', mt: '2px', mb: '2px' }}>
                <Typography sx={{ fontSize: '0.72rem', lineHeight: 1 }}>🚚</Typography>
                <Typography
                  noWrap
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    lineHeight: 1.2,
                    color: openStatus.status === 'closed' ? '#94a3b8' : '#166534',
                  }}
                >
                  {truckLine}
                </Typography>
              </Box>
            )}

            {/* Info Row 1: Rating + distance + prep time */}
            <Box className={classes.infoLine}>
              <StarRoundedIcon className={classes.starIcon} />
              <span className={classes.ratingValue}>{rating.toFixed(1)}</span>
              <span className={classes.reviewCount}>({reviewCount})</span>
              {distanceMiles !== null && (
                <>
                  <span className={classes.dot}>•</span>
                  <span className={classes.metaText}>{distanceMiles} mi</span>
                </>
              )}
              <span className={classes.dot}>•</span>
              <span className={classes.metaText}>{prepTime} min prep</span>
            </Box>

            {/* Catering page: the notice this restaurant needs. */}
            {cateringLine && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mt: '4px' }}>
                <RoomServiceRoundedIcon sx={{ fontSize: 14, color: CATERING_COLOR }} />
                <Typography noWrap sx={{ fontSize: '0.75rem', fontWeight: 700, color: CATERING_COLOR, lineHeight: 1.3 }}>
                  {cateringLine}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </CardActionArea>
    </MuiCard>
  );
}

export default React.memo(Card);
